/**
 * OpenAI Voice Service for Events Explorer
 * Handles Speech-to-Text (Whisper) and Text-to-Speech (TTS)
 * Routes through Laravel API proxy — no direct OpenAI calls from browser.
 */

import { $apiV1 } from '../../../config/api';
import { generateFuzzyCandidates, applyFuzzyCorrections, getAvailableRoster } from '../../../utils/playerNameMatcher';

// --- Voice Language Settings ---

const VOICE_LANGUAGE_KEY = 'ai_voice_language';

// Popular languages first, then alphabetical. All 99 Whisper-supported languages.
export const VOICE_LANGUAGES = [
  { code: 'auto', label: 'Auto-detect' },
  // --- Popular ---
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'sr', label: 'Serbian' },
  { code: 'hr', label: 'Croatian' },
  { code: 'nl', label: 'Dutch' },
  { code: 'tr', label: 'Turkish' },
  { code: 'pl', label: 'Polish' },
  { code: 'ar', label: 'Arabic' },
  { code: 'ru', label: 'Russian' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'zh', label: 'Chinese' },
  // --- All others (alphabetical) ---
  { code: 'af', label: 'Afrikaans' },
  { code: 'sq', label: 'Albanian' },
  { code: 'am', label: 'Amharic' },
  { code: 'hy', label: 'Armenian' },
  { code: 'as', label: 'Assamese' },
  { code: 'az', label: 'Azerbaijani' },
  { code: 'ba', label: 'Bashkir' },
  { code: 'eu', label: 'Basque' },
  { code: 'be', label: 'Belarusian' },
  { code: 'bn', label: 'Bengali' },
  { code: 'bs', label: 'Bosnian' },
  { code: 'br', label: 'Breton' },
  { code: 'bg', label: 'Bulgarian' },
  { code: 'yue', label: 'Cantonese' },
  { code: 'ca', label: 'Catalan' },
  { code: 'cs', label: 'Czech' },
  { code: 'da', label: 'Danish' },
  { code: 'et', label: 'Estonian' },
  { code: 'fo', label: 'Faroese' },
  { code: 'fi', label: 'Finnish' },
  { code: 'gl', label: 'Galician' },
  { code: 'ka', label: 'Georgian' },
  { code: 'el', label: 'Greek' },
  { code: 'gu', label: 'Gujarati' },
  { code: 'ht', label: 'Haitian Creole' },
  { code: 'ha', label: 'Hausa' },
  { code: 'haw', label: 'Hawaiian' },
  { code: 'he', label: 'Hebrew' },
  { code: 'hi', label: 'Hindi' },
  { code: 'hu', label: 'Hungarian' },
  { code: 'is', label: 'Icelandic' },
  { code: 'id', label: 'Indonesian' },
  { code: 'jw', label: 'Javanese' },
  { code: 'kn', label: 'Kannada' },
  { code: 'kk', label: 'Kazakh' },
  { code: 'km', label: 'Khmer' },
  { code: 'lo', label: 'Lao' },
  { code: 'la', label: 'Latin' },
  { code: 'lv', label: 'Latvian' },
  { code: 'ln', label: 'Lingala' },
  { code: 'lt', label: 'Lithuanian' },
  { code: 'lb', label: 'Luxembourgish' },
  { code: 'mk', label: 'Macedonian' },
  { code: 'mg', label: 'Malagasy' },
  { code: 'ms', label: 'Malay' },
  { code: 'ml', label: 'Malayalam' },
  { code: 'mt', label: 'Maltese' },
  { code: 'mi', label: 'Maori' },
  { code: 'mr', label: 'Marathi' },
  { code: 'mn', label: 'Mongolian' },
  { code: 'my', label: 'Myanmar' },
  { code: 'ne', label: 'Nepali' },
  { code: 'no', label: 'Norwegian' },
  { code: 'nn', label: 'Nynorsk' },
  { code: 'oc', label: 'Occitan' },
  { code: 'ps', label: 'Pashto' },
  { code: 'fa', label: 'Persian' },
  { code: 'pa', label: 'Punjabi' },
  { code: 'ro', label: 'Romanian' },
  { code: 'sa', label: 'Sanskrit' },
  { code: 'sn', label: 'Shona' },
  { code: 'sd', label: 'Sindhi' },
  { code: 'si', label: 'Sinhala' },
  { code: 'sk', label: 'Slovak' },
  { code: 'sl', label: 'Slovenian' },
  { code: 'so', label: 'Somali' },
  { code: 'su', label: 'Sundanese' },
  { code: 'sw', label: 'Swahili' },
  { code: 'sv', label: 'Swedish' },
  { code: 'tl', label: 'Tagalog' },
  { code: 'tg', label: 'Tajik' },
  { code: 'ta', label: 'Tamil' },
  { code: 'tt', label: 'Tatar' },
  { code: 'te', label: 'Telugu' },
  { code: 'th', label: 'Thai' },
  { code: 'bo', label: 'Tibetan' },
  { code: 'tk', label: 'Turkmen' },
  { code: 'uk', label: 'Ukrainian' },
  { code: 'ur', label: 'Urdu' },
  { code: 'uz', label: 'Uzbek' },
  { code: 'vi', label: 'Vietnamese' },
  { code: 'cy', label: 'Welsh' },
  { code: 'yi', label: 'Yiddish' },
  { code: 'yo', label: 'Yoruba' },
];

// Shared source of truth for the AI voice toggle. Both the Global AI panel and
// the Events Explorer AI panel persist the toggle to this key, so reading it
// fresh at speak-time guarantees a "voice off" set anywhere always wins —
// regardless of any panel's (possibly stale) in-memory/closure state.
export const VOICE_ENABLED_KEY = 'ai_voice_enabled';

export const isVoiceOutputEnabled = () => {
  try {
    const v = localStorage.getItem(VOICE_ENABLED_KEY);
    return v === null ? true : v === 'true';
  } catch {
    return true;
  }
};

export const getVoiceLanguage = () => {
  try {
    return localStorage.getItem(VOICE_LANGUAGE_KEY) || 'auto';
  } catch {
    return 'auto';
  }
};

export const setVoiceLanguage = (code) => {
  try {
    localStorage.setItem(VOICE_LANGUAGE_KEY, code);
  } catch {}
};

// --- Search History ---

const SEARCH_HISTORY_KEY = 'ai_voice_search_history';
const MAX_HISTORY = 50;

/**
 * Track a successfully searched player name in localStorage.
 * @param {string} name
 */
export const trackSearchedPlayer = (name) => {
  try {
    const raw = localStorage.getItem(SEARCH_HISTORY_KEY);
    const history = raw ? JSON.parse(raw) : [];
    // Remove duplicate if exists, then prepend
    const filtered = history.filter(n => n !== name);
    filtered.unshift(name);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(filtered.slice(0, MAX_HISTORY)));
  } catch {}
};

/**
 * Get recent unique player name searches.
 * @param {number} limit
 * @returns {string[]}
 */
export const getRecentSearches = (limit = 15) => {
  try {
    const raw = localStorage.getItem(SEARCH_HISTORY_KEY);
    return raw ? JSON.parse(raw).slice(0, limit) : [];
  } catch {
    return [];
  }
};

// --- Dynamic Whisper Prompt ---

const BASE_PROMPT = 'Football analytics: goals, assists, shots, passes, key passes, crosses, dribbles, tackles, interceptions, fouls, cards, duels, aerial duels, clearances, offsides, ball recoveries, progressive passes, progressive carries, set pieces, corners, free kicks, forwards, midfielders, defenders, goalkeeper.';

/**
 * Build a context-aware Whisper prompt with player names.
 * Whisper prompt limit: 224 tokens (~40 player names fit).
 * Names placed at the end are weighted most heavily by Whisper.
 * Merges context names + recent search history (history at end for highest weight).
 * @param {string[]} contextPlayerNames - Player names from current context
 * @returns {string}
 */
export const buildWhisperPrompt = (contextPlayerNames = []) => {
  const recentSearches = getRecentSearches(15);
  const allNames = [...contextPlayerNames, ...recentSearches];

  if (allNames.length === 0) return BASE_PROMPT;

  // Deduplicate (preserving order — later entries stay, which means history names end up last = highest weight)
  const seen = new Set();
  const unique = [];
  for (let i = allNames.length - 1; i >= 0; i--) {
    if (!seen.has(allNames[i])) {
      seen.add(allNames[i]);
      unique.unshift(allNames[i]);
    }
  }

  // Limit to 40 names total
  return `${BASE_PROMPT} Players: ${unique.slice(0, 40).join(', ')}.`;
};

/**
 * Transcribe audio to text using OpenAI Whisper via API proxy
 * @param {Blob} audioBlob - The audio blob to transcribe
 * @param {string} [language='auto'] - Language code for Whisper
 * @param {string[]} [contextPlayerNames=[]] - Player names for prompt context
 * @returns {Promise<string>} - The transcribed text
 */
export const transcribeAudio = async (audioBlob, language = 'auto', contextPlayerNames = []) => {
  const formData = new FormData();
  formData.append('file', audioBlob, 'recording.webm');
  formData.append('model', 'gpt-4o-mini-transcribe');
  formData.append('prompt', buildWhisperPrompt(contextPlayerNames));

  if (language !== 'auto') {
    formData.append('language', language);
  }

  const response = await $apiV1.post('/ai/transcribe', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000
  });

  return response.data.text;
};

/**
 * Convert text to speech using OpenAI TTS via API proxy
 * @param {string} text - The text to speak
 * @returns {Promise<Blob>} - The audio blob
 */
export const textToSpeech = async (text) => {
  const response = await $apiV1.post('/ai/speech', {
    model: 'tts-1',
    voice: 'nova',
    input: text
  }, {
    responseType: 'blob',
    timeout: 30000
  });

  return response.data;
};

/**
 * Parse a dual response containing both screen text and voice text.
 * Voice text is wrapped in [VOICE_RESPONSE]...[/VOICE_RESPONSE] tags.
 * @param {string} fullText - The full AI response text
 * @returns {{ screenText: string, voiceText: string|null }}
 */
export const parseDualResponse = (fullText) => {
  const match = fullText.match(/\[VOICE_RESPONSE\]([\s\S]*?)\[\/VOICE_RESPONSE\]/);

  if (!match) {
    return { screenText: fullText, voiceText: null };
  }

  const voiceText = match[1].trim();
  const screenText = fullText.replace(/\s*\[VOICE_RESPONSE\][\s\S]*?\[\/VOICE_RESPONSE\]\s*/, '').trim();

  return { screenText, voiceText };
};

// --- Claude Haiku Name Correction Pipeline ---

const haikuPost = async (data, maxRetries = 2) => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await $apiV1.post('/ai/messages', data, { timeout: 30000 });
    } catch (err) {
      const status = err.response?.status;
      if ((status === 529 || status === 429) && attempt < maxRetries) {
        await new Promise(r => setTimeout(r, (attempt + 1) * 2000));
        continue;
      }
      throw err;
    }
  }
};

const HAIKU_MODEL = 'claude-haiku-4-5-20251001';

/**
 * Mode B, Pass 1: Extract team/league/country context from raw transcript.
 * Also provides a general-knowledge correction (Mode C fallback).
 * @param {string} rawTranscript
 * @returns {Promise<{team: string|null, league: string|null, country: string|null, corrected_text: string}>}
 */
export const extractTeamContext = async (rawTranscript) => {
  try {
    const response = await haikuPost({
      model: HAIKU_MODEL,
      max_tokens: 256,
      system: `You analyze voice-transcribed football search queries. Extract any team name, league name, or country mentioned. Fix misspelled player names using your knowledge of football players worldwide. Pay special attention to Eastern European, Slavic, and non-English names. If no team/league/country context is found, leave them empty strings. CRITICAL: corrected_text must be the COMPLETE original sentence — never shorten, summarize, or extract just the name.`,
      messages: [{ role: 'user', content: rawTranscript }],
      tool_choice: { type: 'tool', name: 'extract_context' },
      tools: [{
        name: 'extract_context',
        description: 'Return extracted context and corrected text from a voice-transcribed football query.',
        input_schema: {
          type: 'object',
          properties: {
            team: { type: 'string', description: 'Team name mentioned, or empty string if none' },
            league: { type: 'string', description: 'League name mentioned, or empty string if none' },
            country: { type: 'string', description: 'Country mentioned, or empty string if none' },
            corrected_text: { type: 'string', description: 'The COMPLETE original sentence repeated verbatim, with ONLY misspelled player names corrected. Never shorten or omit any words.' }
          },
          required: ['team', 'league', 'country', 'corrected_text']
        }
      }],
    });

    const toolBlock = response.data?.content?.find(b => b.type === 'tool_use');
    if (toolBlock?.input) {
      // Guard against Haiku truncating the sentence — if corrected text is much shorter, keep original
      let corrected = toolBlock.input.corrected_text || rawTranscript;
      if (corrected.length < rawTranscript.length * 0.75) {
        corrected = rawTranscript;
      }
      return {
        team: toolBlock.input.team || null,
        league: toolBlock.input.league || null,
        country: toolBlock.input.country || null,
        corrected_text: corrected
      };
    }
    return { team: null, league: null, country: null, corrected_text: rawTranscript };
  } catch (err) {
    console.error('extractTeamContext failed:', err);
    return { team: null, league: null, country: null, corrected_text: rawTranscript };
  }
};

/**
 * Correct player names using Claude Haiku with optional roster and fuzzy candidates.
 * Used in Mode A (local roster) and Mode B (fetched roster).
 * @param {string} rawTranscript
 * @param {string[]} playerNames - Player roster names
 * @param {Array} fuzzyCandidates - From generateFuzzyCandidates()
 * @returns {Promise<string>}
 */
export const correctPlayerNames = async (rawTranscript, playerNames = [], fuzzyCandidates = []) => {
  const hasRoster = playerNames.length > 0;
  const hasCandidates = fuzzyCandidates.length > 0;

  let contextSection = '';
  if (hasRoster) {
    contextSection += `\nPlayer roster:\n${playerNames.join(', ')}`;
  }
  if (hasCandidates) {
    const candidateLines = fuzzyCandidates.map(c =>
      `"${c.transcribed}" might be: ${c.candidates.join(', ')}`
    ).join('\n');
    contextSection += `\n\nFuzzy match suggestions (ranked by confidence):\n${candidateLines}`;
  }

  const basePrompt = hasRoster
    ? `You are a football transcription corrector. Correct any player names to match the closest name from the roster below. The fuzzy match suggestions are algorithmically generated hints — use them as guidance but make your own judgment. Only fix names — do not alter anything else.`
    : `You are a football transcription corrector. Using your knowledge of football players worldwide, correct any misspelled player names. Pay special attention to Eastern European, Slavic, and non-English names.${hasCandidates ? ' The fuzzy match suggestions below are algorithmically generated hints.' : ''} Only fix names — do not alter anything else.`;

  try {
    const response = await haikuPost({
      model: HAIKU_MODEL,
      max_tokens: 256,
      system: basePrompt + contextSection,
      messages: [{ role: 'user', content: rawTranscript }],
      tool_choice: { type: 'tool', name: 'correct_text' },
      tools: [{
        name: 'correct_text',
        description: 'Return the corrected version of the voice-transcribed text with player names fixed. The corrected_text MUST be the complete original sentence.',
        input_schema: {
          type: 'object',
          properties: {
            corrected_text: { type: 'string', description: 'The COMPLETE original sentence repeated verbatim, with ONLY misspelled player names corrected. Never shorten, summarize, or extract just the name — return the full sentence.' }
          },
          required: ['corrected_text']
        }
      }],
    });

    const toolBlock = response.data?.content?.find(b => b.type === 'tool_use');
    const corrected = toolBlock?.input?.corrected_text || rawTranscript;
    // Guard against Haiku truncating the sentence
    return corrected.length < rawTranscript.length * 0.75 ? rawTranscript : corrected;
  } catch (err) {
    console.error('correctPlayerNames failed:', err);
    return rawTranscript;
  }
};

/**
 * Search for teams via API (standalone, not through AI tool chain).
 * @param {string} query
 * @returns {Promise<Array<{id: number, name: string}>>}
 */
const searchTeamsForContext = async (query) => {
  try {
    const response = await $apiV1.get('/platform/team/', {
      params: { search: query, page_size: 5 }
    });
    return (response.data?.results || []).map(t => ({ id: t.id, name: t.name }));
  } catch {
    return [];
  }
};

/**
 * Fetch team roster via API (standalone, lightweight — names only).
 * @param {number} teamId
 * @returns {Promise<Array<{name: string}>>}
 */
const fetchRosterForContext = async (teamId) => {
  try {
    const response = await $apiV1.get(`/statistics/team_opt/${teamId}`);
    const players = response.data?.players_list || [];
    return players.map(p => ({ name: p.display_name || p.name })).filter(p => p.name);
  } catch {
    return [];
  }
};

/**
 * Main voice transcription correction pipeline.
 * Orchestrates 3 modes:
 *   Mode A: local roster + fuzzy + Haiku (1 Haiku call)
 *   Mode B: team hint extraction → roster fetch → Haiku (2 Haiku calls + 2 API calls)
 *   Mode C: general knowledge only (1 Haiku call)
 *
 * @param {string} rawTranscript - Raw Whisper transcription
 * @returns {Promise<string>} - Corrected transcript
 */
export const processVoiceTranscription = async (rawTranscript) => {
  const localRoster = getAvailableRoster();

  // --- Mode A: Local roster available ---
  if (localRoster.length > 0) {
    const fuzzyCandidates = generateFuzzyCandidates(rawTranscript, localRoster);

    // High-confidence shortcut: skip Haiku if all fuzzy matches are strong
    if (fuzzyCandidates.length > 0 && fuzzyCandidates.every(c => c.topScore > 0.85)) {
      return applyFuzzyCorrections(rawTranscript, fuzzyCandidates);
    }

    // Otherwise: Haiku with roster + candidates (1 Haiku call)
    const rosterNames = localRoster.map(p => p.name);
    return await correctPlayerNames(rawTranscript, rosterNames, fuzzyCandidates);
  }

  // --- Mode B or C: No local roster ---
  // Haiku #1: extract team context + general correction
  const context = await extractTeamContext(rawTranscript);

  if (context.team) {
    // Mode B: team detected — fetch roster, then do informed correction
    const teams = await searchTeamsForContext(context.team);
    if (teams.length > 0) {
      const roster = await fetchRosterForContext(teams[0].id);
      if (roster.length > 0) {
        const fuzzyCandidates = generateFuzzyCandidates(rawTranscript, roster);
        const rosterNames = roster.map(p => p.name);
        return await correctPlayerNames(rawTranscript, rosterNames, fuzzyCandidates);
        // Total: 2 Haiku calls + 2 API calls
      }
    }
  }

  // Mode C: no context — use Haiku #1's general correction
  return context.corrected_text;
};

// --- Fallback Search Retries ---

// Module-level retry queue, set by processVoiceTranscription pipeline
let _currentRetryQueue = [];

/**
 * Strip diacritics from a string (e.g. "Kovačević" → "Kovacevic").
 * @param {string} str
 * @returns {string}
 */
const stripDiacritics = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Build a deduplicated retry queue of up to 5 search variations.
 * Sources ranked by expected quality: fuzzy candidates > raw Whisper > surname-only > ASCII-normalized.
 * @param {string} rawWhisperName - Original Whisper transcription of the name
 * @param {string} haikuCorrectedName - Haiku-corrected name
 * @param {Array<{candidates: string[]}>} fuzzyCandidates - From generateFuzzyCandidates()
 * @returns {string[]}
 */
export const buildRetryQueue = (rawWhisperName, haikuCorrectedName, fuzzyCandidates = []) => {
  const variations = new Set();

  // 1. Fuzzy match candidates (best quality, from local roster)
  for (const c of fuzzyCandidates) {
    for (const name of c.candidates) {
      variations.add(name);
    }
  }

  // 2. Raw Whisper text (if different from Haiku correction)
  if (rawWhisperName && rawWhisperName !== haikuCorrectedName) {
    variations.add(rawWhisperName);
  }

  // 3. Surname-only variant (users often search by last name)
  const parts = haikuCorrectedName.split(/\s+/);
  if (parts.length > 1) {
    variations.add(parts[parts.length - 1]);
  }

  // 4. ASCII-normalized variant (strip diacritics)
  const normalized = stripDiacritics(haikuCorrectedName);
  if (normalized !== haikuCorrectedName) {
    variations.add(normalized);
  }

  return [...variations].slice(0, 5);
};

/**
 * Set the current retry queue (called after processVoiceTranscription).
 * @param {string[]} queue
 */
export const setRetryQueue = (queue) => {
  _currentRetryQueue = queue;
};

/**
 * Get and clear the current retry queue.
 * @returns {string[]}
 */
export const consumeRetryQueue = () => {
  const queue = _currentRetryQueue;
  _currentRetryQueue = [];
  return queue;
};
